import { MarkupType, SelectedAlbum, PackageLineItem } from '../types';

// ── MARKUP CALCULATION ───────────────────────────────────────────
export function calculateMarkup(totalCost: number, markupType: MarkupType, customPercent?: number): { markupAmount: number; quotedAmount: number; markupPercent: number } {
  let markupPercent = 0;
  if (markupType === 'npnl') markupPercent = 0;
  else if (markupType === 'custom') markupPercent = customPercent || 0;
  else markupPercent = markupType;

  const markupAmount = Math.round(totalCost * (markupPercent / 100));
  const quotedAmount = totalCost + markupAmount;
  return { markupAmount, quotedAmount, markupPercent };
}

// ── ALBUM COST ───────────────────────────────────────────────────
export function calculateAlbumCost(album: Partial<SelectedAlbum>): number {
  if (!album.sheets || !album.paperRatePerSheet || !album.coverRate) return 0;
  const paperCost = album.sheets * album.paperRatePerSheet;
  const coverCost = album.coverRate;
  const designCost = album.designChargeAmount || 0;
  return (paperCost + coverCost + designCost) * (album.numAlbums || 1);
}

// ── SHEET VALIDATION ─────────────────────────────────────────────
export function validateAlbumSheets(album: SelectedAlbum): { status: 'within' | 'exceeded'; revisedCost?: number; difference?: number } {
  const actual = album.actualSheets;
  if (!actual) return { status: 'within' };
  if (actual <= (album.sheets || 0)) return { status: 'within' };

  const extraSheets = actual - (album.sheets || 0);
  const extraCost = extraSheets * album.paperRatePerSheet * (album.numAlbums || 1);
  const revisedAlbumCost = (album.totalAlbumCost || 0) + extraCost;
  return { status: 'exceeded', revisedCost: revisedAlbumCost, difference: extraCost };
}

// ── QUOTE NUMBER ─────────────────────────────────────────────────
export function generateInvoiceNumber(): string {
  const now=new Date();
  const yy=String(now.getFullYear()).slice(-2);
  const mm=String(now.getMonth()+1).padStart(2,'0');
  const dd=String(now.getDate()).padStart(2,'0');
  const rand=Math.floor(Math.random()*9000)+1000;
  return `SS-INV-${yy}${mm}${dd}-${rand}`;
}

export function generateQuoteNumber(): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const rand = Math.floor(Math.random() * 9000) + 1000;
  return `SS-${yy}${mm}${dd}-${rand}`;
}

// ── FORMAT CURRENCY ──────────────────────────────────────────────
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
}

// ── SERVICE DISPLAY NAMES ────────────────────────────────────────
export const SERVICE_NAMES: Record<string, string> = {
  pre_wedding:  'Pre Wedding Visual Session',
  wedding:      'Wedding Visual Session',
  engagement:   'Engagement / Marriage Anniversary Shoot',
  birthday:     'Birthday Shoot',
  office_event: 'Office / Any Related Event',
};

export const SERVICE_SHORT: Record<string, string> = {
  pre_wedding:  'Pre Wedding',
  wedding:      'Wedding',
  engagement:   'Engagement',
  birthday:     'Birthday',
  office_event: 'Office/Event',
};

// ── COVERAGE TYPE DISPLAY ────────────────────────────────────────
export const COVERAGE_DESCRIPTIONS: Record<string, string> = {
  'Traditional Photography': 'Full event photo shoot — all ceremonies and moments',
  'Traditional Video':       'Full event video recording — all ceremonies',
  'Candid Photography':      'Candid & candid-style photography throughout the event',
  'Cinematography':          'Cinematic videography — artistic, film-style coverage',
  'Fusion Photography':      'Semi-candid + traditional photography combined',
  'Fusion Film':             'Semi-cinematography + traditional video combined',
};

// ── PACKAGE TYPE INFO ────────────────────────────────────────────
export const PACKAGE_TYPE_INFO: Record<string, string> = {
  'Fixed':              'Studio decides everything — curated options presented to customer',
  'Semi-Fixed':         'Studio decides core, customer chooses from curated options',
  'Fully Personalized': 'Customer builds from scratch — full freedom of choice',
};

// ── LOCAL STORAGE KEYS ───────────────────────────────────────────
export const STORAGE_KEYS = {
  QUOTES:         'ss_quotes',
  WALK_IN_ALBUM:  'ss_walk_in_album',
  WALK_IN_PP:     'ss_walk_in_pp',
  GENERAL_PLANNER:'ss_general_planner',
  CURRENT_USER:   'ss_current_user',
  BOOKINGS:       'ss_bookings',
  LIFECYCLE_TOKEN:'ss_lifecycle_token',
};

// ── GENERAL PLANNER → STANDARD QUOTE SHAPE ─────────────────────────
// General Planner quotes are saved with their own flat shape (contractors/
// ppVendors/album/eventDates at the top level). Dashboard, QuoteHistory and
// BookingCalendar all read from the standard Quote shape (packageItems[]).
// Rather than touching every consumer, wrap each GP quote into a single
// synthetic packageItem so it shows up everywhere a normal quote does.
export function normalizeGeneralPlannerQuote(gp: any): any {
  return {
    ...gp,
    isGeneralPlanner: true,
    packageItems: [
      {
        id: `${gp.id}_main`,
        serviceType: 'general',
        serviceName: gp.eventName || 'Custom / Ad-hoc Shoot',
        qualityTier: 'General',
        packageType: 'General Planner',
        productCode: gp.quoteNumber,
        productName: 'Custom / Ad-hoc Shoot',
        eventDates: gp.eventDates || [],
        coverageTypes: [],
        contractors: (gp.contractors || []).map((c: any) => ({ contractor: c.name, deliverable: c.deliverable, rate: c.rate })),
        ppVendors: (gp.ppVendors || []).map((p: any) => ({ vendor: p.name, deliverable: p.deliverable, rate: p.rate, qty: p.qty })),
        album: gp.album,
        addOns: [],
        subtotal: gp.totalCost,
      },
    ],
  };
}


// ── SAVE / LOAD HELPERS ──────────────────────────────────────────
export function saveToStorage<T>(key: string, data: T): void {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch (e) { console.error('Storage save failed:', e); }
}

export function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
}
